import { darken, lighten, SimplePaletteColorOptions } from '@mui/material/styles';
import tinycolor from 'tinycolor2';

const getContrastText = (hex: string) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

export const generateColorVariants = (mainLight: string, mainDark?: string) => {
  const base = mainLight;

  const default50 = lighten(base, 0.9);
  const default100 = lighten(base, 0.8);
  const default200 = lighten(base, 0.6);
  const default300 = lighten(base, 0.4);
  const default400 = lighten(base, 0.2);
  const default500 = base; // main
  const default600 = darken(base, 0.2);
  const default700 = darken(base, 0.4);
  const default800 = darken(base, 0.6);
  const default900 = darken(base, 0.8);

  const defaultMain = default500;

  const defaultSet: SimplePaletteColorOptions = {
    lighter: tinycolor(default100).toHexString() as string,
    light: tinycolor(default300).toHexString() as string,
    main: tinycolor(defaultMain).toHexString() as string,
    dark: tinycolor(default700).toHexString() as string,
    darker: tinycolor(default900).toHexString() as string,
    contrastText: getContrastText(defaultMain),

    50: tinycolor(default50).toHexString() as string,
    100: tinycolor(default100).toHexString() as string,
    200: tinycolor(default200).toHexString() as string,
    300: tinycolor(default300).toHexString() as string,
    400: tinycolor(default400).toHexString() as string,
    500: tinycolor(default500).toHexString() as string,
    600: tinycolor(default600).toHexString() as string,
    700: tinycolor(default700).toHexString() as string,
    800: tinycolor(default800).toHexString() as string,
    900: tinycolor(default900).toHexString()
  };

  // -------------------------------------------------
  // Dark mode: start from a slightly darker "base"
  // -------------------------------------------------
  const darkBase = mainDark ?? darken(base, 0.18);

  const dark50 = lighten(darkBase, 0.45);
  const dark100 = lighten(darkBase, 0.35);
  const dark200 = lighten(darkBase, 0.25);
  const dark300 = lighten(darkBase, 0.18);
  const dark400 = lighten(darkBase, 0.1);
  const dark500 = darkBase;
  const dark600 = darken(darkBase, 0.06);
  const dark700 = darken(darkBase, 0.14);
  const dark800 = darken(darkBase, 0.22);
  const dark900 = darken(darkBase, 0.3);

  const darkMain = dark500;

  const darkSet: SimplePaletteColorOptions = {
    lighter: tinycolor(dark100).toHexString() as string,
    light: tinycolor(dark300).toHexString() as string,
    main: tinycolor(darkMain).toHexString() as string,
    dark: tinycolor(dark700).toHexString() as string,
    darker: tinycolor(dark900).toHexString() as string,
    contrastText: getContrastText(darkMain),

    50: tinycolor(dark50).toHexString() as string,
    100: tinycolor(dark100).toHexString() as string,
    200: tinycolor(dark200).toHexString() as string,
    300: tinycolor(dark300).toHexString() as string,
    400: tinycolor(dark400).toHexString() as string,
    500: tinycolor(dark500).toHexString() as string,
    600: tinycolor(dark600).toHexString() as string,
    700: tinycolor(dark700).toHexString() as string,
    800: tinycolor(dark800).toHexString() as string,
    900: tinycolor(dark900).toHexString() as string
  };

  return { default: defaultSet, dark: darkSet };
};
